import { ActionRuleParams } from '../../types';
import { addIslandVents, deleteIslandVents } from '../../../islands/helpers';
import { BlueprintInstanceType } from '../../../shared.enums';
import { Connection } from '../../../connections/connection.class';
import { Island } from '../../../islands/island.class';
import { isIslandVentingRequired } from '../../helpers';
import { isMokIsland } from '../../../panel-trims/helpers';

// Resolves a connection's target to a connected Island, skipping obstruction targets.
const getConnectedIsland = (connection: Connection): Island | null => {
	const { targetIsland } = connection;
	if (!targetIsland || targetIsland.instanceOf !== BlueprintInstanceType.Island) return null;
	return targetIsland as Island;
};

const applyVentingForIsland = (island: Island, params: ActionRuleParams): boolean => {
	const { refMap } = params;
	deleteIslandVents({ island, refMap });

	const ventsNeeded = isIslandVentingRequired({ island, refMap });

	if (ventsNeeded) {
		addIslandVents({ island, refMap });
	}

	return ventsNeeded;
};

// Adds vents to every island connected to the given MOK island that has fuel appliances.
const applyVentingToMokAdjacentIslands = (sourceIsland: Island, params: ActionRuleParams): void => {
	const { refMap } = params;

	for (const connection of sourceIsland.connections.children) {
		const adjacentIsland = getConnectedIsland(connection);
		if (!adjacentIsland) continue;

		addIslandVents({ island: adjacentIsland, refMap });
	}
};

// Adds vents to island if it is connected to any MOK island that has fuel appliances.
const applyVentingFromMokAdjacentIslands = (island: Island, params: ActionRuleParams): void => {
	const { refMap } = params;

	for (const connection of island.connections.children) {
		const adjacentIsland = getConnectedIsland(connection);
		if (!adjacentIsland) continue;
		if (!isMokIsland(adjacentIsland)) continue;

		if (isIslandVentingRequired({ island: adjacentIsland, refMap })) {
			addIslandVents({ island, refMap });
			return;
		}
	}
};

export const requiresVenting = (params: ActionRuleParams): void => {
	const { refMap } = params;
	const { island } = refMap;

	// single-island scope -- apply directly, then propagate to/from MOK adjacent islands
	if (island) {
		const ventsNeeded = applyVentingForIsland(island, params);
		if (ventsNeeded && isMokIsland(island)) {
			applyVentingToMokAdjacentIslands(island, params);
		} else if (!ventsNeeded) {
			applyVentingFromMokAdjacentIslands(island, params);
		}
		return;
	}

	// kitchen scope -- two passes to prevent the second pass from wiping vents added in the first
	const islands = refMap.islands ?? refMap.kitchen?.design?.islands ?? null;
	if (!islands) return;

	// pass 1: apply venting based on each island's own fuel-sourced appliances
	const mokIslandsNeedingVents = new Array<Island>();
	for (const child of islands.children) {
		const ventsNeeded = applyVentingForIsland(child as Island, params);
		if (ventsNeeded && isMokIsland(child as Island)) {
			mokIslandsNeedingVents.push(child as Island);
		}
	}

	// pass 2: add vents to islands adjacent (gap < 1") to any MOK island that has fuel appliances
	for (const mokIsland of mokIslandsNeedingVents) {
		applyVentingToMokAdjacentIslands(mokIsland, params);
	}
};
